import { Button } from '@/renderer/components/common';
import { OutletTable } from '@/renderer/types';
import { OrderShiftModalProps } from '@/renderer/types/sales';
import { Search, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { AlertCard } from '../common/AlertCard';

const normalizeText = (value: string): string => value.trim().toLowerCase();

export const OrderShiftModal: React.FC<OrderShiftModalProps> = ({
  isOpen,
  order,
  sections,
  isSubmitting = false,
  onClose,
  onSubmit,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTable, setSelectedTable] = useState<OutletTable | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setSelectedTable(null);
    }
  }, [isOpen]);

  const availableSections = useMemo(() => {
    const query = normalizeText(searchTerm);

    return sections
      .map((section) => {
        const tables = section.tables.filter((table) => {
          if (table.has_running_order) return false;
          if (!query) return true;

          return normalizeText(table.name).includes(query);
        });

        return {
          ...section,
          tables,
        };
      })
      .filter((section) => section.tables.length > 0);
  }, [searchTerm, sections]);

  const hasAvailableTables = availableSections.length > 0;

  const handleSubmit = async () => {
    if (!selectedTable?.id || isSubmitting) return;
    await onSubmit({ targetTableId: selectedTable.id });
  };

  if (!isOpen || !order) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
      <div
        className="absolute inset-0 bg-black/45 backdrop-blur-[1px]"
        role="button"
        tabIndex={0}
        onClick={() => {
          if (!isSubmitting) onClose();
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && !isSubmitting) onClose();
        }}
        aria-label="Close order shift modal"
      />

      <div className="relative flex w-full max-w-3xl flex-col overflow-hidden rounded-md bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <AlertCard
            title="Shit Items"
            description={`All items will be moved from Table ${order.table_name} to the new table`}
          />
          <button
            type="button"
            className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-black disabled:opacity-50"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Close shift order modal"
          >
            <X size={20} />
          </button>
        </div>

        <div className="border-b border-gray-200 p-3">
          <div className="relative">
            <Search
              size={14}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search table"
              className="h-9 w-full rounded border border-gray-300 pl-9 pr-3 text-sm text-black outline-none placeholder:text-gray-400 focus:border-gray-400 focus:ring-2 focus:ring-gray-300"
            />
          </div>
        </div>

        <div className="max-h-[55vh] space-y-3 overflow-y-auto p-3">
          {!hasAvailableTables ? (
            <div className="rounded border border-gray-200 p-4 text-center text-sm text-black/70">
              No empty tables available for shifting.
            </div>
          ) : (
            availableSections.map((section) => (
              <div
                key={section.id}
                className="rounded border border-gray-200 bg-white"
              >
                <div className="app-bg border-b border-gray-200 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-black">
                  {section.name}
                </div>

                <div className="grid grid-cols-2 gap-2 p-2 md:grid-cols-3">
                  {section.tables.map((table) => {
                    const isSelected = selectedTable?.id === table.id;

                    return (
                      <button
                        key={table.id}
                        type="button"
                        className={`rounded border px-3 py-2 text-sm font-medium transition-colors ${
                          isSelected
                            ? 'border-primary bg-primary text-white'
                            : 'border-gray-300 bg-white text-black hover:border-gray-400'
                        }`}
                        onClick={() => setSelectedTable(table)}
                      >
                        {table.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="border-t border-gray-200 p-3">
          <Button
            type="button"
            className="h-10 w-full text-xs font-semibold uppercase tracking-wide"
            onClick={handleSubmit}
            disabled={!selectedTable?.id || !hasAvailableTables || isSubmitting}
            isLoading={isSubmitting}
          >
            {selectedTable?.name
              ? `Move items to table ${selectedTable.name}`
              : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
};
